import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaPlus, FaEdit, FaTrash, FaPrescriptionBottle, FaSearch, FaPills, FaUserMd, FaUserAlt, FaCalendarAlt, FaPaperclip, FaArrowLeft } from 'react-icons/fa';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import BottomNav from '../components/BottomNav';
import { useApp, useAutoRefresh, triggerRefresh } from '../context/AppContext';
import FileAttachment from '../components/FileAttachment';
import RecordCard from '../components/RecordCard';
import { getPrescriptions, createPrescription, updatePrescription, deletePrescription, getPatients, getDoctors, getFileUrl, MAX_ATTACHMENT_SIZE, MAX_ATTACHMENT_SIZE_MESSAGE } from '../services/api';

const EMPTY_MED = { name: '', dosage: '', duration: '', instructions: '' };

const EMPTY_FORM = {
  patientName: '', patientId: '', doctorId: '',
  date: new Date().toISOString().split('T')[0],
  age: '',
  diagnosis: '',
  complaint: '',
  previousSurgicalHistory: '',
  previousInvestigations: '',
  pastIssues: '',
  coMorbidities: '',
  managementPlan: '',
  drugHistory: '',
  bloodInvestigations: '',
  urineTest: '',
  radiologicalInvestigations: '',
  semenInvestigations: '',
  specialInvestigations: '',
  sitzBath: '',
  medicines: [{ ...EMPTY_MED }],
  notes: '',
};

const CLINICAL_FIELDS = [
  { key: 'complaint',                 label: 'Complaint' },
  { key: 'previousSurgicalHistory',   label: 'Previous Surgical History' },
  { key: 'previousInvestigations',    label: 'Previous Investigations' },
  { key: 'pastIssues',                label: 'Past Issues' },
  { key: 'coMorbidities',             label: 'Co-Morbidities' },
  { key: 'managementPlan',            label: 'Management Plan' },
  { key: 'drugHistory',               label: 'Drug History' },
  { key: 'bloodInvestigations',       label: 'Blood Investigations' },
  { key: 'urineTest',                 label: 'URINE Test' },
  { key: 'radiologicalInvestigations',label: 'Radiological Investigations' },
  { key: 'semenInvestigations',       label: 'Semen Investigations' },
  { key: 'specialInvestigations',     label: 'Special Investigations' },
  { key: 'sitzBath',                  label: 'Sitz Bath' },
];

const Prescriptions = () => {
  const { showToast, user } = useApp();
  const role = user?.role || 'Doctor';
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [editId, setEditId] = useState(null);
  const [, setSelectedPatientObj] = useState(null);
  const [search, setSearch] = useState('');
  const [filterDoctor, setFilterDoctor] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const fileRef = useRef();

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const [rxRes, patRes, docRes] = await Promise.allSettled([
        getPrescriptions(),
        role !== 'Patient' ? getPatients() : Promise.resolve({ data: { data: [] } }),
        role !== 'Doctor'  ? getDoctors() : Promise.resolve({ data: { data: [] } }),
      ]);
      setItems(rxRes.status === 'fulfilled' ? (rxRes.value.data?.data || []) : []);
      if (patRes.status === 'fulfilled') setPatients(patRes.value.data?.data || []);
      if (docRes.status === 'fulfilled') setDoctors(docRes.value.data?.data || []);
    } catch {
      setItems([]);
    }
    setLoading(false);
  }, [role]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useAutoRefresh(fetchItems, 'prescriptions');

  const addMedicine = () => setForm(p => ({ ...p, medicines: [...p.medicines, { ...EMPTY_MED }] }));
  const removeMedicine = (i) => setForm(p => ({ ...p, medicines: p.medicines.filter((_, idx) => idx !== i) }));
  const updateMedicine = (i, field, val) => setForm(p => ({
    ...p,
    medicines: p.medicines.map((m, idx) => idx === i ? { ...m, [field]: val } : m),
  }));

  const setField = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const buildFormData = () => {
    const fd = new FormData();
    const skip = ['medicines'];
    Object.entries(form).forEach(([k, v]) => {
      if (!skip.includes(k)) fd.append(k, v ?? '');
    });
    fd.append('medicines', JSON.stringify(form.medicines));
    if (editId) fd.append('existingAttachments', JSON.stringify(existingAttachments));
    selectedFiles.forEach(f => fd.append('files', f));
    return fd;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = (selectedFiles.length > 0 || editId) ? buildFormData() : form;
      if (editId) {
        const res = await updatePrescription(editId, payload);
        setItems(prev => prev.map(i => (i.id === editId || i._id === editId) ? (res.data?.data || { ...i, ...form }) : i));
        showToast('Prescription updated!', 'success');
      } else {
        const res = await createPrescription(payload);
        setItems(prev => [res.data?.data || res.data, ...prev]);
        showToast('Prescription created!', 'success');
        triggerRefresh('prescriptions');
      }
      resetForm();
    } catch (err) {
      showToast(err.message || 'Failed to save', 'error');
    }
    setSaving(false);
  };

  const resetForm = () => {
    setShowModal(false);
    setEditId(null);
    setSelectedFiles([]);
    setExistingAttachments([]);
    setSelectedPatientObj(null);
    setForm({ ...EMPTY_FORM, date: new Date().toISOString().split('T')[0] });
  };

  const handlePatientSelect = (e) => {
    const pObj = patients.find(pt => String(pt.userId) === e.target.value || String(pt.id) === e.target.value);
    const pid = pObj?.userId ? String(pObj.userId) : e.target.value;
    setSelectedPatientObj(pObj || null);
    setForm(prev => ({
      ...prev,
      patientId: pid,
      patientName: pObj ? pObj.name : '',
      age: pObj?.age != null ? String(pObj.age) : prev.age,
    }));
  };

  const handleEdit = (item) => {
    const meds = item.medicines || item.PrescriptionMedicines || [];
    const pObj = patients.find(pt => String(pt.userId) === String(item.patientId) || String(pt.id) === String(item.patientId));
    setSelectedPatientObj(pObj || null);
    const restored = { ...EMPTY_FORM };
    Object.keys(EMPTY_FORM).forEach(k => {
      if (k === 'medicines') return;
      restored[k] = item[k] != null ? String(item[k]) : '';
    });
    restored.medicines = meds.length
      ? meds.map(m => ({ name: m.name || '', dosage: m.dosage || '', duration: m.duration || '', instructions: m.instructions || '' }))
      : [{ ...EMPTY_MED }];
    setForm(restored);
    setSelectedFiles([]);
    setExistingAttachments(Array.isArray(item.attachments) && item.attachments.length > 0 ? item.attachments : (item.file_url ? [{ url: item.file_url, name: null }] : []));
    setEditId(item._id || item.id);
    setShowModal(true);
  };

  const handleView = (item) => { setViewItem(item); setShowViewModal(true); };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this prescription?')) return;
    try {
      await deletePrescription(id);
      setItems(prev => prev.filter(i => i._id !== id && i.id !== id));
      showToast('Prescription deleted', 'error');
    } catch (err) { showToast(err.message || 'Delete failed', 'error'); }
  };

  const getDoctorName = (item) =>
    item.doctor?.name ||
    (role === 'Doctor' && item.doctorId === user?.id ? user.name : null) ||
    doctors.find(d => d.id === item.doctorId)?.name ||
    (item.doctorId ? `Dr. #${item.doctorId}` : null);

  const getPatientPhoto = (item) => {
    if (role === 'Patient') return getFileUrl(user?.profileImage) || null;
    const p = patients.find(pt => String(pt.userId) === String(item.patientId) || String(pt.id) === String(item.patientId));
    return getFileUrl(p?.account?.profileImage) || null;
  };

  const filtered = items.filter(i => {
    if (search) {
      const q = search.toLowerCase();
      if (role === 'Patient') {
        const doctorName = (i.doctor?.name || doctors.find(d => d.id === i.doctorId)?.name || '').toLowerCase();
        const medNames = (i.medicines || i.PrescriptionMedicines || []).map(m => m.name || '').join(' ').toLowerCase();
        const diagnosis = (i.diagnosis || '').toLowerCase();
        const complaint = (i.complaint || '').toLowerCase();
        const notes = (i.notes || '').toLowerCase();
        const matched = doctorName.includes(q) || medNames.includes(q) || diagnosis.includes(q) || complaint.includes(q) || notes.includes(q);
        if (!matched) return false;
      } else {
        if (!i.patientName?.toLowerCase().includes(q)) return false;
      }
    }
    if (filterDoctor && String(i.doctorId) !== String(filterDoctor)) return false;
    if (filterDate && i.date !== filterDate) return false;
    return true;
  });

  // ── FORM VIEW ────────────────────────────────────────────────────────────────
  if (showModal) {
    return (
      <div className="page-wrapper">
        <Header title={editId ? 'Edit Prescription' : 'New Prescription'} />
        <Sidebar />
        <main className="page-content">
          <div className="page-header">
            <div>
              <h1><FaPrescriptionBottle style={{ color: 'var(--primary)', marginRight: 8 }} />{editId ? 'Edit Prescription' : 'New Prescription'}</h1>
              <p>{editId ? 'Update prescription details' : 'Create a new prescription'}</p>
            </div>
            <button className="btn btn-outline" onClick={resetForm}><FaArrowLeft /> Back</button>
          </div>

          <div className="card" style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
            <form onSubmit={handleSubmit}>

              {/* Patient + Date + Age */}
              <div className="form-grid">
                <div className="form-group">
                  <label>Patient *</label>
                  <select required value={form.patientId} onChange={handlePatientSelect}>
                    <option value="">Select patient...</option>
                    {patients.map(p => (
                      <option key={p.id} value={p.userId || p.id}>{p.name}{p.userId ? ' (Registered)' : ''}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={form.date} onChange={e => setField('date', e.target.value)} />
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Age</label>
                  <input
                    type="number"
                    min="0"
                    max="150"
                    placeholder="Auto-filled from patient"
                    value={form.age}
                    onChange={e => setField('age', e.target.value)}
                  />
                </div>
                {role === 'Admin' && doctors.length > 0 && (
                  <div className="form-group">
                    <label>Doctor</label>
                    <select value={form.doctorId} onChange={e => setField('doctorId', e.target.value)}>
                      <option value="">Select doctor...</option>
                      {doctors.map(d => (
                        <option key={d.id} value={d.id}>{d.name}{d.specialization ? ` — ${d.specialization}` : ''}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Diagnosis */}
              <div className="form-group">
                <label>Diagnosis</label>
                <textarea rows={2} value={form.diagnosis} onChange={e => setField('diagnosis', e.target.value)} placeholder="Primary diagnosis..." />
              </div>

              {/* Clinical fields */}
              <div className="section-title" style={{ marginTop: 16 }}>Clinical Details</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {CLINICAL_FIELDS.map(({ key, label }) => (
                  <div className="form-group" key={key} style={{ marginBottom: 0 }}>
                    <label>{label}</label>
                    <textarea
                      rows={2}
                      value={form[key]}
                      onChange={e => setField(key, e.target.value)}
                      placeholder={label + '...'}
                      style={{ resize: 'vertical', minHeight: 60 }}
                    />
                  </div>
                ))}
              </div>

              {/* Medicines */}
              <div className="section-title" style={{ marginTop: 20 }}>Medicines</div>
              {form.medicines.map((med, i) => (
                <div key={i} style={{ background: 'var(--primary-bg)', borderRadius: 10, padding: 12, marginBottom: 10 }}>
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label>Medicine Name *</label>
                    <input required value={med.name} onChange={e => updateMedicine(i, 'name', e.target.value)} placeholder="e.g. Metformin 500mg" />
                  </div>
                  <div className="form-grid">
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Dosage</label>
                      <input value={med.dosage} onChange={e => updateMedicine(i, 'dosage', e.target.value)} placeholder="e.g. 1-0-1" />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Duration</label>
                      <input value={med.duration} onChange={e => updateMedicine(i, 'duration', e.target.value)} placeholder="e.g. 30 days" />
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, marginTop: 8 }}>
                    <label>Instructions</label>
                    <input value={med.instructions} onChange={e => updateMedicine(i, 'instructions', e.target.value)} placeholder="e.g. After meals" />
                  </div>
                  {form.medicines.length > 1 && (
                    <button type="button" onClick={() => removeMedicine(i)} className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 12, marginTop: 8 }}>Remove</button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addMedicine} className="btn btn-outline" style={{ width: '100%', marginBottom: 16 }}>
                <FaPlus /> Add Another Medicine
              </button>

              {/* Notes + Attachment */}
              <div className="form-group">
                <label>Notes</label>
                <textarea value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="Special instructions..." />
              </div>
              <div className="form-group">
                <label><FaPaperclip style={{ marginRight: 6 }} />Attachment (PDF, Image, Word)</label>
                {existingAttachments.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                    {existingAttachments.map((att, i) => (
                      <FileAttachment key={i} url={att.url} filename={att.name} compact={true}
                        onRemove={() => setExistingAttachments(prev => prev.filter((_, idx) => idx !== i))} />
                    ))}
                  </div>
                )}
                <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => {
                  const files = Array.from(e.target.files);
                  if (files.some(f => f.size > MAX_ATTACHMENT_SIZE)) {
                    showToast(MAX_ATTACHMENT_SIZE_MESSAGE, 'error');
                    e.target.value = '';
                    return;
                  }
                  setSelectedFiles(files);
                }} style={{ padding: '8px 0', fontSize: 13 }} />
                {selectedFiles.length > 0 && <div style={{ fontSize: 12, color: 'var(--primary)', marginTop: 4 }}>{selectedFiles.map(f => f.name).join(', ')}</div>}
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={resetForm}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving}>{saving ? 'Saving...' : editId ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  // ── LIST VIEW ────────────────────────────────────────────────────────────────
  return (
    <div className="page-wrapper">
      <Header title="Prescriptions" />
      <Sidebar />
      <main className="page-content">
        <div className="page-header">
          <div>
            <h1><FaPrescriptionBottle style={{ color: 'var(--primary)', marginRight: 8 }} />Prescriptions</h1>
            <p>{filtered.length} prescription{filtered.length !== 1 ? 's' : ''}</p>
          </div>
          {(role === 'Doctor' || role === 'Admin') && (
            <button className="btn btn-primary" onClick={() => { setEditId(null); setSelectedFiles([]); setExistingAttachments([]); setSelectedPatientObj(null); setForm({ ...EMPTY_FORM, date: new Date().toISOString().split('T')[0] }); setShowModal(true); }}>
              <FaPlus /> New
            </button>
          )}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="search-bar" style={{ flex: 1, minWidth: 180 }}>
            <FaSearch />
            <input placeholder={role === 'Patient' ? 'Search by doctor, medicine, diagnosis...' : 'Search by patient name...'} value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {role !== 'Doctor' && doctors.length > 0 && (
            <select
              value={filterDoctor}
              onChange={e => setFilterDoctor(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, minWidth: 160 }}
            >
              <option value="">All Doctors</option>
              {doctors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
          <input
            type="date"
            value={filterDate}
            onChange={e => setFilterDate(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}
            title="Filter by date"
          />
          {(filterDoctor || filterDate) && (
            <button className="btn btn-outline" style={{ padding: '8px 12px', fontSize: 12 }} onClick={() => { setFilterDoctor(''); setFilterDate(''); }}>
              Clear Filters
            </button>
          )}
        </div>

        {loading ? <div className="loading-container"><div className="spinner" /></div> :
          filtered.length === 0 ? <div className="empty-state"><FaPrescriptionBottle className="empty-state-icon" /><h3>No prescriptions found</h3></div> :
          <div className="record-grid">
            {filtered.map(item => {
              const doctorName = getDoctorName(item);
              const meds = item.PrescriptionMedicines || item.medicines || [];
              const meta = [];
              if (item.age != null) meta.push({ icon: <FaUserAlt size={11} />, text: `Age: ${item.age}` });
              meta.push({ icon: <FaCalendarAlt size={11} />, text: item.date ? new Date(item.date).toLocaleDateString('en-IN') : '-' });
              if (doctorName) meta.push({ icon: <FaUserMd size={11} />, text: doctorName });
              const hasAttachments = (Array.isArray(item.attachments) && item.attachments.length > 0) || item.file_url;
              return (
                <RecordCard
                  key={item._id || item.id}
                  avatarLetter={(item.patientName || '?')[0].toUpperCase()}
                  avatarImage={getPatientPhoto(item)}
                  title={item.patientName}
                  meta={meta}
                  subLabel={item.diagnosis ? `Dx: ${item.diagnosis}` : null}
                  items={meds.map(m => ({
                    icon: <FaPills size={14} />,
                    name: m.name,
                    badges: [m.dosage, m.duration, m.instructions].filter(Boolean),
                  }))}
                  note={item.notes}
                  timestamp={item.createdAt ? `Added on ${new Date(item.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} • ${new Date(item.createdAt).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}` : null}
                  onView={() => handleView(item)}
                  actions={(role === 'Doctor' || role === 'Admin') && (
                    <>
                      <button className="action-btn action-btn-edit" onClick={() => handleEdit(item)}><FaEdit /></button>
                      <button className="action-btn action-btn-delete" onClick={() => handleDelete(item._id || item.id)}><FaTrash /></button>
                    </>
                  )}
                >
                  {hasAttachments && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                      {item.attachments && Array.isArray(item.attachments) && item.attachments.map((att, i) => (
                        <FileAttachment key={i} url={att.url} filename={att.name} compact={true} />
                      ))}
                      {(!Array.isArray(item.attachments) || item.attachments.length === 0) && item.file_url && (
                        <FileAttachment url={item.file_url} compact={true} />
                      )}
                    </div>
                  )}
                </RecordCard>
              );
            })}
          </div>
        }
      </main>
      <BottomNav />

      {/* VIEW MODAL */}
      {showViewModal && viewItem && (
        <div className="side-panel-overlay" onClick={e => e.target === e.currentTarget && setShowViewModal(false)}>
          <div className="side-panel">
            <div className="modal-header">
              <h2>Prescription Details</h2>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>×</button>
            </div>

            {/* Header info */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15, paddingBottom: 10, borderBottom: '1px solid #eee' }}>
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1 }}>Patient</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--primary)' }}>{viewItem.patientName}</div>
                {viewItem.age != null && <div style={{ fontSize: 13, color: 'var(--text-medium)' }}>Age: {viewItem.age}</div>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1 }}>Date</div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{viewItem.date ? new Date(viewItem.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</div>
                {viewItem.doctor?.name && (
                  <div style={{ fontSize: 13, color: 'var(--text-medium)', marginTop: 4 }}>
                    <FaUserMd style={{ marginRight: 4 }} />{viewItem.doctor.name}
                  </div>
                )}
              </div>
            </div>

            {/* Diagnosis */}
            {viewItem.diagnosis && (
              <ViewSection label="Diagnosis" value={viewItem.diagnosis} color="#e8f5e9" textColor="#2e7d32" />
            )}

            {/* Clinical fields */}
            {CLINICAL_FIELDS.map(({ key, label }) =>
              viewItem[key] ? <ViewSection key={key} label={label} value={viewItem[key]} /> : null
            )}

            {/* Medicines */}
            <div style={{ marginBottom: 15 }}>
              <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Prescribed Medicines</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(viewItem.PrescriptionMedicines || viewItem.medicines || []).map((m, i) => (
                  <div key={i} style={{ background: '#f8f9fa', padding: 12, borderRadius: 10, borderLeft: '4px solid var(--primary)' }}>
                    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{m.name}</div>
                    <div style={{ display: 'flex', gap: 15, fontSize: 13, color: 'var(--text-medium)', flexWrap: 'wrap' }}>
                      {m.dosage && <span><strong>Dosage:</strong> {m.dosage}</span>}
                      {m.duration && <span><strong>Duration:</strong> {m.duration}</span>}
                    </div>
                    {m.instructions && <div style={{ fontSize: 12, marginTop: 4, fontStyle: 'italic', color: 'var(--text-medium)' }}>Instructions: {m.instructions}</div>}
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            {viewItem.notes && (
              <div style={{ marginBottom: 15 }}>
                <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 }}>Doctor's Notes</div>
                <div style={{ background: '#fff9c4', padding: 10, borderRadius: 8, fontSize: 14, color: '#5d4037' }}>{viewItem.notes}</div>
              </div>
            )}

            {/* Attachments */}
            {((Array.isArray(viewItem.attachments) && viewItem.attachments.length > 0) || viewItem.file_url) && (
              <div style={{ marginBottom: 15 }}>
                <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Attachments</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {Array.isArray(viewItem.attachments) && viewItem.attachments.map((att, i) => (
                    <FileAttachment key={i} url={att.url} filename={att.name} />
                  ))}
                  {!Array.isArray(viewItem.attachments) && viewItem.file_url && <FileAttachment url={viewItem.file_url} />}
                </div>
              </div>
            )}

            <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowViewModal(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

const ViewSection = ({ label, value, color = '#f8f9fa', textColor = 'inherit' }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{label}</div>
    <div style={{ background: color, padding: '8px 12px', borderRadius: 8, fontSize: 14, color: textColor, whiteSpace: 'pre-wrap' }}>{value}</div>
  </div>
);

export default Prescriptions;
